/**
 * 旧PCRM と同じ線のアイコン（lucide-react：fill none・stroke currentColor・線幅 2）。
 * 旧は lucide-react をボタンの中で 14〜20px で使っており、MUI の塗りのアイコン（20〜24px）とは見た目が違うため、
 * 新でも lucide に揃える（277 2 回目）。呼び出し側は MUI のアイコンと同じ書き方（fontSize・sx・color）で使える。
 * - 大きさ：sx.fontSize（数値・'14px'・'14px !important'）＞ size ＞ fontSize（small=14・large=24・inherit=1em）＞ 部品ごとの既定
 * - 色：既定は文字色（currentColor）。color は MUI と同じ名前（action・primary など）
 */
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Columns3,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Info,
  ListFilter,
  Mail,
  Menu,
  MoreHorizontal,
  Palette,
  Paperclip,
  Pencil,
  Phone,
  Plug,
  Plus,
  Reply,
  Search,
  Send,
  Star,
  Trash2,
  Upload,
  User,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { forwardRef } from 'react';
import type { AriaAttributes, CSSProperties } from 'react';

export interface LegacyIconProps extends AriaAttributes {
  /** px（MUI の SvgIcon には無い。直接大きさを決めたいとき） */
  size?: number;
  fontSize?: 'small' | 'medium' | 'large' | 'inherit';
  color?: 'inherit' | 'action' | 'disabled' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  sx?: SxProps<Theme>;
  className?: string;
  style?: CSSProperties;
  strokeWidth?: number;
  /** 塗る（★ のオンなど。旧も fill を付けて塗っていた） */
  filled?: boolean;
}

/** MUI の SvgIcon と同じく、部品を受け取る側（モード一覧など）が型として使う */
export type LegacyIconComponent = ReturnType<typeof makeLegacyIcon>;

function sizeFromSx(sx: SxProps<Theme> | undefined): number | undefined {
  if (sx === null || typeof sx !== 'object' || Array.isArray(sx)) {
    return undefined;
  }
  const font_size = (sx as { fontSize?: unknown }).fontSize;
  if (typeof font_size === 'number') {
    return font_size;
  }
  if (typeof font_size === 'string') {
    const matched = /^(\d+(?:\.\d+)?)px/.exec(font_size.trim());
    return matched ? Number(matched[1]) : undefined;
  }
  return undefined;
}

function colorOf(color: LegacyIconProps['color']): string | undefined {
  switch (color) {
    case undefined:
    case 'inherit':
      return undefined;
    case 'action':
      return 'action.active';
    case 'disabled':
      return 'action.disabled';
    default:
      return `${color}.main`;
  }
}

function sizeOf(props: LegacyIconProps, default_size: number): number | string {
  const from_sx = sizeFromSx(props.sx);
  if (from_sx !== undefined) {
    return from_sx;
  }
  if (props.size !== undefined) {
    return props.size;
  }
  switch (props.fontSize) {
    case 'small':
      return 14;
    case 'large':
      return 24;
    case 'inherit':
      return '1em';
    default:
      return default_size;
  }
}

export function makeLegacyIcon(icon_component: LucideIcon, default_size = 16, display_name = icon_component.displayName ?? 'Icon') {
  const LegacyIcon = forwardRef<SVGSVGElement, LegacyIconProps>(function LegacyIcon(props, ref) {
    const { size: _size, fontSize: _font_size, color, sx, strokeWidth = 2, filled: is_filled = false, ...rest } = props;
    const px = sizeOf(props, default_size);
    const sx_list = (Array.isArray(sx) ? sx : [sx]) as Array<Exclude<SxProps<Theme>, readonly unknown[]>>;
    const is_labelled = rest['aria-label'] !== undefined;
    return (
      <Box
        component={icon_component}
        ref={ref}
        size={px}
        strokeWidth={strokeWidth}
        fill={is_filled ? 'currentColor' : 'none'}
        aria-hidden={is_labelled ? undefined : true}
        role={is_labelled ? 'img' : undefined}
        data-testid={`${display_name}Icon`}
        {...rest}
        sx={[
          { width: px, height: px, flexShrink: 0, fontSize: 'inherit', color: colorOf(color) },
          ...sx_list,
          // sx の fontSize は大きさに読み替え済み。svg の font-size は使わない
          { fontSize: 'inherit !important' },
        ]}
      />
    );
  });
  LegacyIcon.displayName = `Legacy${display_name}Icon`;
  return LegacyIcon;
}

// 旧の使い方の既定の大きさ（旧ソース crm/frontend の lucide の size）
export const AddIcon = makeLegacyIcon(Plus, 14, 'Add');
export const CloseIcon = makeLegacyIcon(X, 20, 'Close');
export const ClearIcon = makeLegacyIcon(X, 14, 'Clear');
export const DownloadIcon = makeLegacyIcon(Download, 16, 'FileDownload');
export const UploadIcon = makeLegacyIcon(Upload, 16, 'FileUpload');
export const EditIcon = makeLegacyIcon(Pencil, 14, 'Edit');
export const DeleteIcon = makeLegacyIcon(Trash2, 14, 'Delete');
export const MailIcon = makeLegacyIcon(Mail, 12, 'Mail');
export const PhoneIcon = makeLegacyIcon(Phone, 12, 'Phone');
export const PersonIcon = makeLegacyIcon(User, 20, 'Person');
export const CardIcon = makeLegacyIcon(CreditCard, 14, 'CreditCard');
export const ChevronLeftIcon = makeLegacyIcon(ChevronLeft, 16, 'ChevronLeft');
export const ChevronRightIcon = makeLegacyIcon(ChevronRight, 16, 'ChevronRight');
export const ExpandMoreIcon = makeLegacyIcon(ChevronDown, 14, 'ExpandMore');
export const ArrowBackIcon = makeLegacyIcon(ArrowLeft, 18, 'ArrowBack');
export const SearchIcon = makeLegacyIcon(Search, 14, 'Search');
export const FilterIcon = makeLegacyIcon(Filter, 12, 'FilterAlt');
export const FilterListIcon = makeLegacyIcon(ListFilter, 16, 'FilterList');
export const MenuIcon = makeLegacyIcon(Menu, 20, 'Menu');
export const InfoIcon = makeLegacyIcon(Info, 16, 'Info');
export const CheckIcon = makeLegacyIcon(Check, 14, 'Check');
export const CopyIcon = makeLegacyIcon(Copy, 16, 'ContentCopy');
export const ClipboardIcon = makeLegacyIcon(Clipboard, 16, 'ContentPaste');
export const PaletteIcon = makeLegacyIcon(Palette, 16, 'Palette');
export const PlugIcon = makeLegacyIcon(Plug, 16, 'Power');
export const OpenIcon = makeLegacyIcon(ExternalLink, 16, 'OpenInNew');
export const SendIcon = makeLegacyIcon(Send, 16, 'Send');
export const ReplyIcon = makeLegacyIcon(Reply, 16, 'Reply');
export const DescriptionIcon = makeLegacyIcon(FileText, 16, 'Description');
export const ViewColumnIcon = makeLegacyIcon(Columns3, 16, 'ViewColumn');
export const MoreHorizIcon = makeLegacyIcon(MoreHorizontal, 16, 'MoreHoriz');
export const AttachFileIcon = makeLegacyIcon(Paperclip, 16, 'AttachFile');
export const ArrowDropDownIcon = makeLegacyIcon(ChevronDown, 14, 'ArrowDropDown');
/** 日付の欄のカレンダー（旧は input type=date の小さなカレンダー） */
export const CalendarIcon = makeLegacyIcon(Calendar, 16, 'Calendar');
/** ★ のオン（塗り）とオフ（線） */
const StarLine = makeLegacyIcon(Star, 14, 'Star');
export const StarFilledIcon = forwardRef<SVGSVGElement, LegacyIconProps>(function StarFilledIcon(props, ref) {
  return <StarLine ref={ref} filled {...props} />;
});
export const StarBorderIcon = makeLegacyIcon(Star, 14, 'StarBorder');
